import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    desc = "desc",
    link = "link",
    label = "label",
    cssClass = "cssClass",
    openInNewWidow = "openInNewWidow",
    buttonBackgroundColor="buttonBackgroundColor",
    buttonTextColor="buttonTextColor",
    
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cssClass]: string;
        [fields.desc]: string;
        [fields.link]: LinkFieldValue;
        [fields.label]: string;
        [fields.openInNewWidow]: boolean;
        [fields.buttonBackgroundColor]: string;
        [fields.buttonTextColor]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SurTabularDataStyles {
    container?: InjectableCss;
    ctacontainer?: InjectableCss;
    title?: TypographyProps;
    descStyles?: TypographyProps;
    ctaLink?: LinkProps;
}
export const tabularDataStyles: SurTabularDataStyles = {
    container: {
        css: css`
            padding: 60px 0;
            max-width: 955px;
            margin: 0 auto;
        `,
    },
    title: {
        variant: "h1",
        css: css`
            text-align: center;
            margin-bottom: 20px;
            font-size: ${TYPOGRAPHY.size.xxlarge};
            line-height: ${TYPOGRAPHY.lineHeight.xlmedium};
            text-transform: uppercase;
            color: ${getColor("text.main")};
        `,
    },
    descStyles: {
        variant: "p",
        css: css`
            text-align: center;
            width: 100%;
            margin: 0 auto;
            max-width: 100%;
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${getColor("text.main")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 20px;
                    `,
                    css``,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    ctacontainer: {
        css: css`
            text-align: center;
            margin-top: 15px;
        `,
    },
   
};

const styles = tabularDataStyles;

const SurInfoContent: React.FC<Props> = ({ fields, id ,session}) => {
    const { url } = useGetLink(fields.link);

    const isLoggedIn = (session.isAuthenticated || session.rememberMe) && !session.isGuest;

    if(isLoggedIn){
        return null;
    }

    const  ctaLink = {
        css: css`
            outline: 0;
            background-color: ${fields.buttonBackgroundColor};
            border: 1px solid ${fields.buttonBackgroundColor};
            height: auto;
            padding: 0px 25.5px;
            width: 100%;
            max-width: max-content;
            display: flex;
            margin: 0 auto;
            height: 40px;
            span {
                text-transform: uppercase;
                color: ${fields.buttonTextColor} !important;
                display: block;
                text-align: center;
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: 15px;
            }
        `,
    }
    return (
        <StyledWrapper {...styles.container}>
            {fields.title ? <Typography {...styles.title}>{fields.title}</Typography> : null}
            {fields.desc ? <Typography {...styles.descStyles}>{fields.desc}</Typography> : null}
            {fields.label && fields.link.value ? (
                <StyledWrapper {...styles.ctacontainer}>
                    <Link
                        href={url}
                        target={fields.openInNewWidow ? `_blank` : `_self`}
                        {...ctaLink}
                        aria-label="open_trade_account"
                        role="button"
                    >
                        {fields.label}
                    </Link>
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

const SurInfoContentWidget: WidgetModule = {
    component:connect(mapStateToProps)(SurInfoContent),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.desc,
                displayName: "Description",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.label,
                fieldType: "General",
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 2,
            },

            {
                name: fields.link,
                displayName: "Button Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
            },
            {
                name: fields.buttonBackgroundColor,
                displayName: "Button Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: ``,
                fieldType: "General",
            },
            {
                name: fields.buttonTextColor,
                displayName: "Button Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.openInNewWidow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default SurInfoContentWidget;
